// apps/web/src/components/settings/RmsFeeConfigSection.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPost } from "../../utils/apiClient";

type ApiGetResponse = {
    exists: boolean;
    config: null | {
        avgMonthlySalesBand: string;
        avgUnitPriceBand: string;
        genre: string;
        updatedAt?: any;
    };
    // APIが options を返しても返さなくても動くようにする（保険）
    options?: {
        avgMonthlySalesBands?: string[];
        avgUnitPriceBands?: string[];
        genres?: string[];
    };
};

// ===== 固定選択肢（あなたの要件どおり）=====
const SALES_BANDS = [
    "～50万円",
    "～100万円",
    "～200万円",
    "～300万円",
    "～500万円",
    "～1000万円",
    "1000万円～3000万円",
    "3000万円～1億円",
    "1億円～5億円",
] as const;

const UNIT_PRICE_BANDS = [
    "～7000円",
    "7000～1.5万円",
    "1.5～2.5万円",
    "2.5～3.5万円",
    "3.5～5万円",
    "5万円～",
] as const;

// （APIと同じGENRESを置く。必要ならあなたのconstantsからimportしてもOK）
const GENRES = [
    "ジュエリー・アクセサリー",
    "食品",
    "インナー・下着・ナイトウェア",
    "水・ソフトドリンク",
    "日本酒・焼酎",
    "靴",
    "レディースファッション",
    "バッグ・小物・ブランド雑貨",
    "メンズファッション",
    "スイーツ・お菓子",
    "ビール・洋酒",
    "美容・コスメ・香水",
    "ペット・ペッドグッズ",
    "医薬品・コンタクト・介護",
    "ダイエット・健康",
    "スポーツ・アウトドア",
    "花・ガーデン・DIY",
    "キッズ・ベビー・マタニティ",
    "カタログギフト・チケット",
    "キッチン用品・食器・調理器具",
    "日用品雑貨・文房具・手芸",
    "本・雑貨・コミック",
    "インテリア・寝具・収納",
    "ホビー",
    "サービス・リフォーム",
    "おもちゃ",
    "住宅・不動産",
    "車用品・バイク用品",
    "腕時計",
    "TV・オーディオ・カメラ",
    "パソコン・周辺機器",
    "スマートフォン・タブレット",
    "家電",
    "CD・DVD",
    "楽器・音響機器",
    "車・バイク",
    "光回線・モバイル通信",
    "テレビゲーム",
    "上記以外",
] as const;

function isOneOf(v: any, list: readonly string[]) {
    return typeof v === "string" && list.includes(v);
}

export function RmsFeeConfigSection() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [exists, setExists] = useState(false);

    // 選択値（バンド文字列）
    const [avgMonthlySalesBand, setAvgMonthlySalesBand] = useState<string>("");
    const [avgUnitPriceBand, setAvgUnitPriceBand] = useState<string>("");
    const [genre, setGenre] = useState<string>("");

    // API options が来たときだけ上書きできるように state も持つ（基本は固定）
    const [salesBands, setSalesBands] = useState<string[]>([...SALES_BANDS]);
    const [unitPriceBands, setUnitPriceBands] = useState<string[]>([...UNIT_PRICE_BANDS]);
    const [genres, setGenres] = useState<string[]>([...GENRES]);

    // 「Firestoreにあるけど選択肢外」などの検知
    const [warning, setWarning] = useState<string>("");

    const loadedOnceRef = useRef(false);

    const isValid = useMemo(() => {
        return !!avgMonthlySalesBand && !!avgUnitPriceBand && !!genre;
    }, [avgMonthlySalesBand, avgUnitPriceBand, genre]);

    async function load() {
        setLoading(true);
        setWarning("");
        try {
            const res = await apiGet("/api/company/settings/rmsFeeConfig");
            if (!res.ok) {
                console.error("GET rmsFeeConfig failed:", res.status);
                return;
            }
            const json = (await res.json()) as ApiGetResponse;

            setExists(!!json.exists);

            // options が来たら採用（来なくても固定で動く）
            if (json?.options) {
                if (Array.isArray(json.options.avgMonthlySalesBands) && json.options.avgMonthlySalesBands.length > 0) {
                    setSalesBands(json.options.avgMonthlySalesBands.map(String));
                }
                if (Array.isArray(json.options.avgUnitPriceBands) && json.options.avgUnitPriceBands.length > 0) {
                    setUnitPriceBands(json.options.avgUnitPriceBands.map(String));
                }
                if (Array.isArray(json.options.genres) && json.options.genres.length > 0) {
                    setGenres(json.options.genres.map(String));
                }
            }

            const cfg = json.config;

            if (cfg) {
                // ★ここが重要：Firestoreの値を state に反映（ただし選択肢外は弾く）
                const sBand = String(cfg.avgMonthlySalesBand ?? "");
                const uBand = String(cfg.avgUnitPriceBand ?? "");
                const g = String(cfg.genre ?? "");

                const warn: string[] = [];

                if (sBand && !isOneOf(sBand, SALES_BANDS as unknown as readonly string[])) {
                    warn.push(`平均月商が選択肢外です: "${sBand}"`);
                }
                if (uBand && !isOneOf(uBand, UNIT_PRICE_BANDS as unknown as readonly string[])) {
                    warn.push(`平均客単価が選択肢外です: "${uBand}"`);
                }
                if (g && !isOneOf(g, GENRES as unknown as readonly string[])) {
                    warn.push(`ジャンルが選択肢外です: "${g}"`);
                }

                setAvgMonthlySalesBand(isOneOf(sBand, salesBands) ? sBand : "");
                setAvgUnitPriceBand(isOneOf(uBand, unitPriceBands) ? uBand : "");
                setGenre(isOneOf(g, genres) ? g : "");

                if (warn.length > 0) {
                    setWarning(
                        "保存データに選択肢外の値がありました。画面上は未選択に戻しています。\n" + warn.join("\n")
                    );
                }
            } else {
                setAvgMonthlySalesBand("");
                setAvgUnitPriceBand("");
                setGenre("");
            }

            loadedOnceRef.current = true;
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function save() {
        if (saving) return;
        setSaving(true);
        try {
            // UI側の最小バリデーション
            if (!avgMonthlySalesBand) {
                alert("平均月商を選択してください");
                return;
            }
            if (!avgUnitPriceBand) {
                alert("平均客単価を選択してください");
                return;
            }
            if (!genre) {
                alert("ジャンルを選択してください");
                return;
            }

            // ★APIの期待キーに合わせる（これが重要）
            const body = {
                avgMonthlySalesBand,
                avgUnitPriceBand,
                genre,
            };

            const res = await apiPost("/api/company/settings/rmsFeeConfig", body);
            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                alert(`保存に失敗しました（${res.status}）\n${txt}`);
                return;
            }

            alert("保存しました");
            await load(); // ★保存後に再読み込みして確実に同期
        } catch (e: any) {
            alert(e?.message ?? String(e));
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return (
            <div className="border p-3 rounded-xl bg-white shadow-sm mt-4">
                <div className="text-sm text-slate-600">手数料計算用の設定を読み込み中…</div>
            </div>
        );
    }

    return (
        <div className="border p-3 rounded-xl bg-white shadow-sm mt-4">
            <div className="flex items-center justify-between">
                <h2 className="text-base font-medium">手数料・ジャンル設定（RMS手数料計算用）</h2>
                <div className="text-[11px] text-slate-500">{exists ? "保存済み" : "未設定"}</div>
            </div>

            <div className="mt-2 text-[12px] text-slate-600 leading-relaxed">
                平均月商・平均客単価は<strong>指定の選択肢から選択</strong>してください。
            </div>

            {warning && (
                <div className="mt-3 whitespace-pre-wrap rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                    {warning}
                </div>
            )}

            <div className="mt-3 flex flex-col gap-3">
                {/* 平均月商 */}
                <div>
                    <div className="text-[12px] text-slate-700 mb-1">平均月商</div>
                    <select
                        value={avgMonthlySalesBand}
                        onChange={(e) => setAvgMonthlySalesBand(e.target.value)}
                        className="border rounded px-2 py-1 w-80"
                    >
                        <option value="">選択してください</option>
                        {salesBands.map((x) => (
                            <option key={x} value={x}>
                                {x}
                            </option>
                        ))}
                    </select>
                    {!avgMonthlySalesBand && loadedOnceRef.current && (
                        <div className="text-[11px] text-amber-700 mt-1">※ 平均月商を選択してください</div>
                    )}
                </div>

                {/* 平均客単価 */}
                <div>
                    <div className="text-[12px] text-slate-700 mb-1">平均客単価</div>
                    <select
                        value={avgUnitPriceBand}
                        onChange={(e) => setAvgUnitPriceBand(e.target.value)}
                        className="border rounded px-2 py-1 w-80"
                    >
                        <option value="">選択してください</option>
                        {unitPriceBands.map((x) => (
                            <option key={x} value={x}>
                                {x}
                            </option>
                        ))}
                    </select>
                    {!avgUnitPriceBand && loadedOnceRef.current && (
                        <div className="text-[11px] text-amber-700 mt-1">※ 平均客単価を選択してください</div>
                    )}
                </div>

                {/* ジャンル */}
                <div>
                    <div className="text-[12px] text-slate-700 mb-1">ジャンル</div>
                    <select value={genre} onChange={(e) => setGenre(e.target.value)} className="border rounded px-2 py-1 w-80">
                        <option value="">選択してください</option>
                        {genres.map((g) => (
                            <option key={g} value={g}>
                                {g}
                            </option>
                        ))}
                    </select>
                    {!genre && loadedOnceRef.current && (
                        <div className="text-[11px] text-amber-700 mt-1">※ ジャンルを選択してください</div>
                    )}
                </div>

                <button
                    onClick={save}
                    disabled={saving}
                    className={`bg-blue-600 text-white px-4 py-1.5 rounded w-full text-sm ${saving ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                >
                    {saving ? "保存中…" : "保存"}
                </button>

                {!isValid && (
                    <div className="text-[11px] text-slate-500">
                        ※ 3項目すべて選択すると保存できます
                    </div>
                )}
            </div>
        </div>
    );
}